import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { fontConfig } from "theme/fontConfig";
import { createPortal } from 'react-dom';

const GAP = 6; // space between the icon and the tooltip
const VIEWPORT_MARGIN = 8; // keep the tooltip this far from the window edges

// The tooltip is always rendered through a portal to <body> with fixed positioning,
// measured after it appears: it opens on the preferred side (`placement`, default
// top), flips to the other side when there is no room and is clamped inside the
// window, so parents with overflow:hidden, modals and drawers can't clip or cut it.
// `useFixed` is still accepted for existing callers (it only changes the default side).
const InfoTooltip = ({ text, placement: placementProp, style, useFixed = false }) => {
  // callers that used `useFixed` without a placement opened below, as before
  const placement = placementProp || (useFixed ? 'bottom' : 'top');
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const popupRef = useRef(null);

  // place the tooltip once its real size is known
  useLayoutEffect(() => {
    if (!visible || !iconRef.current || !popupRef.current) return;
    const icon = iconRef.current.getBoundingClientRect();
    const { width, height } = popupRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    const fits = {
      top: icon.top - GAP - height >= VIEWPORT_MARGIN,
      bottom: icon.bottom + GAP + height <= vh - VIEWPORT_MARGIN,
      left: icon.left - GAP - width >= VIEWPORT_MARGIN,
      right: icon.right + GAP + width <= vw - VIEWPORT_MARGIN,
    };
    const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const side = fits[placement] ? placement : fits[opposite[placement]] ? opposite[placement] : fits.top ? 'top' : 'bottom';

    let top;
    let left;
    if (side === 'top' || side === 'bottom') {
      top = side === 'top' ? icon.top - GAP - height : icon.bottom + GAP;
      left = icon.left + icon.width / 2 - width / 2;
    } else {
      left = side === 'left' ? icon.left - GAP - width : icon.right + GAP;
      top = icon.top + icon.height / 2 - height / 2;
    }
    setPos({
      top: clamp(top, VIEWPORT_MARGIN, vh - height - VIEWPORT_MARGIN),
      left: clamp(left, VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN),
    });
  }, [visible, placement, text]);

  // close on scroll / resize / click so it never stays open in the wrong place
  useEffect(() => {
    if (!visible) return undefined;
    const hide = () => setVisible(false);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('mousedown', hide, true);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('mousedown', hide, true);
    };
  }, [visible]);

  const show = () => {
    setPos(null);
    setVisible(true);
  };

  return (
    <span style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0, padding: 0, margin: 0, fontSize: '10px', lineHeight: 1, ...style }}>
      <span
        ref={iconRef}
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: 'rgba(0, 46, 92, 0.08)',
          border: '1.5px solid rgba(0, 46, 92, 0.35)',
          color: '#002E5C',
          fontSize: '10px',
          fontWeight: '700',
          fontFamily: fontConfig.InfoTooltipTextFont.family,
          cursor: 'help',
          userSelect: 'none',
          // read-only ("view") forms disable pointer events on whole rows; the hint stays usable
          pointerEvents: 'auto',
          lineHeight: 1,
          boxSizing: 'border-box',
          paddingTop: 0,
          paddingRight: 0,
          paddingBottom: '0.5px',
          paddingLeft: '0.5px',
          margin: 0,
          flexShrink: 0,
        }}
      >
        ?
      </span>
      {visible &&
        createPortal(
          <span
            ref={popupRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              // measured invisibly first, then shown in place
              visibility: pos ? 'visible' : 'hidden',
              zIndex: 99999,
              backgroundColor: '#fff',
              color: '#333',
              fontSize: '12px',
              fontWeight: 400,
              fontFamily: fontConfig.InfoTooltipTextFont.family,
              textTransform: 'none',
              letterSpacing: 'normal',
              textAlign: 'left',
              lineHeight: '1.6',
              padding: '6px 10px',
              borderRadius: '4px',
              // keep the hint's own line breaks, never break inside a word
              whiteSpace: 'pre-line',
              wordBreak: 'normal',
              overflowWrap: 'normal',
              maxWidth: '280px',
              width: 'max-content',
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            }}
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
};

export default InfoTooltip;
